<script setup lang="ts">
  // Closed-table-session card for the history Mesas tab (port of the -pos
  // PosHistoryTableSessions card): "Mesa {name}" + invoice-state chip,
  // closedAt · orders count, footer total + chevron.
  import { computed, getCurrentInstance } from 'vue';
  import type { ISessionListItem } from '@/domain/pos/closed-session-formatting';
  import { getSessionInvoiceStateInfo } from '@/domain/pos/closed-session-formatting';
  import { usePosMoney } from '@/composition/use-pos-money';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = defineProps<{
    session: ISessionListItem;
  }>();

  const emit = defineEmits<{
    (e: 'click', session: ISessionListItem): void;
  }>();

  const money = usePosMoney();
  const proxy = getCurrentInstance()!.proxy;

  const stateInfo = computed(() =>
    getSessionInvoiceStateInfo(props.session.invoiceState)
  );

  const closedAtText = computed(() =>
    new Date(props.session.closedAt).toLocaleString(proxy.$i18n.locale, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  );

  const ordersCountText = computed(() =>
    String(
      proxy.$tc('posV2.sessionOrdersCount', props.session.ordersCount, {
        count: props.session.ordersCount,
      })
    )
  );
</script>

<template>
  <button
    type="button"
    class="pos-v2-history-session-card"
    @click="emit('click', session)"
  >
    <div class="card-header">
      <div class="header-left">
        <PosUiIcon name="table-furniture" class="table-icon" />
        <span class="session-title">
          {{ $t('posV2.sessionCardTitle', { name: session.tableName }) }}
        </span>
      </div>
      <PosUiBadge :color="stateInfo.color" variant="soft" size="xs">
        {{ $t(stateInfo.i18nKey) }}
      </PosUiBadge>
    </div>

    <div class="card-details">
      <span class="closed-at">{{ closedAtText }}</span>
      <span class="details-separator">·</span>
      <span class="orders-count">{{ ordersCountText }}</span>
    </div>

    <div class="card-footer">
      <span class="total-value">
        {{ money.formatComputedMoney(session.total) }}
      </span>
      <PosUiIcon name="chevron-right" class="chevron-icon" />
    </div>
  </button>
</template>

<style scoped lang="scss">
  .pos-v2-history-session-card {
    @apply tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-3 tw-text-left tw-transition-colors tw-duration-150;

    &:hover {
      @apply tw-border-neutral-300 tw-bg-neutral-50;
    }

    .card-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-2;

      .header-left {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1.5;

        .table-icon {
          @apply tw-flex-none tw-text-base tw-text-neutral-400;
        }

        .session-title {
          @apply tw-min-w-0 tw-truncate tw-text-sm tw-font-bold tw-text-neutral-900;
        }
      }
    }

    .card-details {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-neutral-500;

      .details-separator {
        @apply tw-text-neutral-300;
      }
    }

    .card-footer {
      @apply tw-flex tw-items-center tw-justify-between tw-border-t tw-border-neutral-100 tw-pt-2;

      .total-value {
        @apply tw-text-sm tw-font-bold tw-tabular-nums tw-text-neutral-900;
      }

      .chevron-icon {
        @apply tw-text-lg tw-text-neutral-300;
      }
    }
  }
</style>
